import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SquareFoot from '@mui/icons-material/SquareFoot';
import { apiService } from '../services/api';

export interface DeepLawnData {
  lawnSqFt?: number;
  drivewaySqFt?: number;
  sidewalkSqFt?: number;
  lotSqFt?: string;
  measurementImage?: string;
  fetchedAt?: string;
}

interface DeepLawnMeasurementsDialogProps {
  open: boolean;
  onClose: () => void;
  serviceRequestId: string;
  propertyType: string;
  address?: string;
  initialData?: DeepLawnData | null;
  onDataFetched?: (data: DeepLawnData) => void;
}

const ELIGIBLE_PROPERTY_TYPES = ['single_residential', 'vacant_lot'];

export default function DeepLawnMeasurementsDialog({
  open,
  onClose,
  serviceRequestId,
  propertyType,
  address,
  initialData,
  onDataFetched,
}: DeepLawnMeasurementsDialogProps) {
  const [data, setData] = useState<DeepLawnData | null>(initialData ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configChecked, setConfigChecked] = useState(false);
  const [configured, setConfigured] = useState(false);

  const isEligible = ELIGIBLE_PROPERTY_TYPES.includes(propertyType);

  useEffect(() => {
    if (!open) return;
    setData(initialData ?? null);
    setError(null);
    if (!isEligible) return;
    const check = async () => {
      try {
        const r = await apiService.get('/service-requests/deeplawn-config');
        setConfigured(!!(r as any).configured);
      } catch {
        setConfigured(false);
      } finally {
        setConfigChecked(true);
      }
    };
    check();
  }, [open, isEligible, initialData]);

  const handleFetch = async () => {
    if (!serviceRequestId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.post(`/service-requests/${serviceRequestId}/fetch-deeplawn`);
      const payload = (res as any).data;
      if (payload) {
        setData(payload);
        onDataFetched?.(payload);
      }
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to fetch measurements';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const hasData = data && (data.lawnSqFt != null || data.drivewaySqFt != null || data.sidewalkSqFt != null || data.lotSqFt);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SquareFoot />
          <Typography variant="h6">Property Measurements</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!isEligible && (
          <Typography color="text.secondary">
            Measurements are only available for Single Family Residential and Vacant Lot properties.
          </Typography>
        )}
        {isEligible && !configChecked && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {isEligible && configChecked && !configured && (
          <Typography color="text.secondary">
            Measurements are not configured. Contact support to enable DeepLawn for your account.
          </Typography>
        )}
        {isEligible && configChecked && configured && !hasData && !loading && (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Fetch Lawn, Driveway, and Sidewalk measurements from DeepLawn for this address.
            </Typography>
            {address && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {address}
              </Typography>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Button variant="contained" onClick={handleFetch} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SquareFoot />}>
              {loading ? 'Fetching…' : 'Fetch Measurements'}
            </Button>
          </Box>
        )}
        {isEligible && hasData && (
          <Box sx={{ pt: 0 }}>
            {data?.lawnSqFt != null && (
              <Typography sx={{ mb: 1 }}>
                <strong>Lawn:</strong> {data.lawnSqFt.toLocaleString()} sq ft
              </Typography>
            )}
            {data?.drivewaySqFt != null && (
              <Typography sx={{ mb: 1 }}>
                <strong>Driveway:</strong> {data.drivewaySqFt.toLocaleString()} sq ft
              </Typography>
            )}
            {data?.sidewalkSqFt != null && (
              <Typography sx={{ mb: 1 }}>
                <strong>Sidewalk:</strong> {data.sidewalkSqFt.toLocaleString()} sq ft
              </Typography>
            )}
            {data?.lotSqFt && (
              <Typography sx={{ mb: 1 }}>
                <strong>Lot:</strong> {data.lotSqFt} sq ft
              </Typography>
            )}
            {data?.measurementImage && (
              <Box sx={{ mt: 2 }}>
                <Button size="small" href={data.measurementImage} target="_blank" rel="noopener noreferrer">
                  View measurement image
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {hasData && configured && (
          <Button variant="outlined" size="small" onClick={handleFetch} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
